import { UploadFileTypeEnum } from 'api-hooks/upload/upload.model';
import { PlusCircleSVG, TrashSVG } from 'common/assets';
import stylesColors from 'common/styles/styles.colors';
import { FormState, Input } from 'components/elements';
import { Button } from 'components/elements/buttons';
import Text from 'components/elements/text';
import {
  CardSection,
  FullContainer,
  SeparatorVertical,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import ApecCard from './apec-card';
import InsuranceVisaCard from './document-card';
import PassportCard from './passport-card';
import {
  onAddNewGuestInsurance,
  onAddNewGuestOther,
  onAddNewGuestVaccine,
  onAddNewGuestVisa,
} from '../../hooks/use-get-default-values';
import { GuestFormType } from '../form-type';

export default function () {
  const { t } = useTranslation();
  const { control } = useFormContext<GuestFormType>();
  const {
    fields: visaFields,
    append: visaAppend,
    remove: visaRemove,
  } = useFieldArray({
    control,
    name: 'visas',
    keyName: 'customId',
  });
  const {
    fields: insuranceFields,
    append: insuranceAppend,
    remove: insuranceRemove,
  } = useFieldArray({
    control,
    name: 'insurances',
    keyName: 'customId',
  });
  const {
    fields: otherFields,
    append: otherAppend,
    remove: otherRemove,
  } = useFieldArray({
    control,
    name: 'others',
    keyName: 'customId',
  });

  const {
    fields: vacineFields,
    append: vacineAppend,
    remove: vacineRemove,
  } = useFieldArray({ control, name: 'vaccines', keyName: 'customId' });

  const onVacineAppend = () => vacineAppend(onAddNewGuestVaccine());
  const onVisaAppend = () => visaAppend(onAddNewGuestVisa());
  const onInsuranceAppend = () => insuranceAppend(onAddNewGuestInsurance());
  const onOtherAppend = () => otherAppend(onAddNewGuestOther());

  const onVisaRemove = (index: number) => visaRemove(index);
  const onInsuranceRemove = (index: number) => insuranceRemove(index);
  const onOtherRemove = (index: number) => otherRemove(index);
  const onVacineRemove = (index: number) => vacineRemove(index);

  return (
    <>
      <PassportCard />

      <SeparatorVertical $margin={32}>
        <FullContainer style={styles.sectionHeader}>
          <Text variant="HeadingXSmall">{t('common:visa')}</Text>
          <FormState>
            {({ editable }) =>
              editable && (
                <Button
                  buttonType="secondary"
                  onClick={onVisaAppend}
                  startEnhancer={(color, size) => (
                    <PlusCircleSVG size={size} color={color} />
                  )}
                >
                  {t('common:add')}
                </Button>
              )
            }
          </FormState>
        </FullContainer>
      </SeparatorVertical>
      {visaFields.length ? (
        visaFields.map((visaField, index) => (
          <InsuranceVisaCard
            key={visaField.customId}
            name={`visas.${index}`}
            onRemove={() => onVisaRemove(index)}
            group="booking"
            type="Visa"
            fileType={UploadFileTypeEnum.GuestVisa}
          />
        ))
      ) : (
        <Text variant="ParagraphSmall">
          {t('common:you_have_no_document_at_this_moment', {
            extra: t('common:visa').toLowerCase(),
          })}
        </Text>
      )}

      <ApecCard />

      <SeparatorVertical $margin={32}>
        <FullContainer style={styles.sectionHeader}>
          <Text variant="HeadingXSmall">{t('common:insurance')}</Text>
          <FormState>
            {({ editable }) =>
              editable && (
                <Button
                  buttonType="secondary"
                  onClick={onInsuranceAppend}
                  startEnhancer={(color, size) => (
                    <PlusCircleSVG size={size} color={color} />
                  )}
                >
                  {t('common:add')}
                </Button>
              )
            }
          </FormState>
        </FullContainer>
      </SeparatorVertical>
      {insuranceFields.length ? (
        insuranceFields.map((insuranceField, index) => (
          <InsuranceVisaCard
            key={insuranceField.customId}
            name={`insurances.${index}`}
            onRemove={() => onInsuranceRemove(index)}
            group="booking"
            type="Insurance"
            fileType={UploadFileTypeEnum.GuestInsurance}
          />
        ))
      ) : (
        <Text variant="ParagraphSmall">
          {t('common:you_have_no_document_at_this_moment', {
            extra: t('common:insurance').toLowerCase(),
          })}
        </Text>
      )}

      <SeparatorVertical $margin={32}>
        <FullContainer style={styles.sectionHeader}>
          <Text variant="HeadingXSmall">{t('common:vaccine_cerficate')}</Text>
          <FormState>
            {({ editable }) =>
              editable && (
                <Button
                  buttonType="secondary"
                  onClick={onVacineAppend}
                  startEnhancer={(color, size) => (
                    <PlusCircleSVG size={size} color={color} />
                  )}
                >
                  {t('common:add')}
                </Button>
              )
            }
          </FormState>
        </FullContainer>
      </SeparatorVertical>
      {vacineFields.length ? (
        vacineFields.map((vacineField, index) => (
          <CardSection
            key={vacineField.customId}
            $style={{ marginBottom: '16px' }}
          >
            <FullContainer>
              <Input
                type="files"
                name={`vaccines.${index}.files`}
                label={t('common:upload_document')}
                valueKey="file"
                previewKey="url"
                fileType={UploadFileTypeEnum.GuestVaccine}
                required
              />
            </FullContainer>

            <FormState>
              {({ editable }) =>
                editable && (
                  <Button
                    buttonType="secondary"
                    startEnhancer={(color, size) => (
                      <TrashSVG
                        width={size}
                        height={size}
                        color={stylesColors.redDefault}
                      />
                    )}
                    isError
                    onClick={() => onVacineRemove(index)}
                  >
                    {t('common:remove')}
                  </Button>
                )
              }
            </FormState>
          </CardSection>
        ))
      ) : (
        <Text variant="ParagraphSmall">
          {t('common:you_have_no_document_at_this_moment', {
            extra: t('common:vaccine_cerficate').toLowerCase(),
          })}
        </Text>
      )}

      <SeparatorVertical $margin={32}>
        <FullContainer style={styles.sectionHeader}>
          <Text variant="HeadingXSmall">{t('common:other_documents')}</Text>
          <FormState>
            {({ editable }) =>
              editable && (
                <Button
                  buttonType="secondary"
                  onClick={onOtherAppend}
                  startEnhancer={(color, size) => (
                    <PlusCircleSVG size={size} color={color} />
                  )}
                >
                  {t('common:add')}
                </Button>
              )
            }
          </FormState>
        </FullContainer>
      </SeparatorVertical>
      {otherFields.length ? (
        otherFields.map((otherField, index) => (
          <InsuranceVisaCard
            key={otherField.customId}
            name={`others.${index}`}
            group="booking"
            onRemove={() => onOtherRemove(index)}
            type="other"
            fileType={UploadFileTypeEnum.GuestOtherDocument}
          />
        ))
      ) : (
        <Text variant="ParagraphSmall">
          {t('common:you_have_no_document_at_this_moment', {
            extra: t('common:other_documents').toLowerCase(),
          })}
        </Text>
      )}
    </>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
};
